<script setup lang="ts">
import { computed } from "vue";

import { getClasses } from "~/js/composables/classes";
import { Analytics } from "~/js/interfaces/analytics";

interface Props {
    content?: string | null;
    disabled?: boolean;
    ga?: Analytics | null;
    showIcon?: boolean | null;
    theme?: "light" | "dark" | null;
    type?: "a" | "button" | "div" | "submit" | null;
    url?: string | null;
    utilities?: string | null;
    utilitiesIcon?: string | null;
    variant?: "small" | "wide" | null;
}

const props = withDefaults(defineProps<Props>(), {
    content: "",
    disabled: false,
    showIcon: true,
    theme: null,
    type: "button",
    url: null,
    utilities: "",
    utilitiesContent: "",
    utilitiesIcon: "",
    variant: "small",
});

const classes = {
    _default: {
        _default:"inline-flex w-full md:min-w-74 md:w-auto",
        alignment: 'justify-center',
        border: 'border border-transparent',
        color: 'text-white bg-blue-500',
        cursor: 'cursor-pointer',
        focus: 'focus:bg-blue-300 focus:outline-none focus:ring-4 focus:ring-blue-200',
        font: 'font-title text-base font-bold tracking-wide uppercase',
        hover: ':hover:border-white hover:bg-blue-450 hover:text-white',
        spacing: 'px-4 py-2 lg:px-8 lg:py-4',
        transition: 'transition ease-in-out duration-300',
    },
};

const getType = () => {
    return props.type == "submit" ? "button" : props.type;
};

const attributes = computed(() => {
    if (props.type === "div") {
        return {}; // No attributes for div
    }

    return {
        title: props.ga?.label ?? props.content,
        "data-tag-category": props.ga?.category ?? "",
        "data-tag-action": props.ga?.action ?? "",
        "data-tag-label": props.ga?.label ?? "",
    };
});
</script>

<template>
    <component
        :is="getType()"
        :class="[
            getClasses(classes._default),
            utilities,
        ]"
        :data-theme="theme"
        :href="url"
        :type="type"
        v-bind="attributes"
    >
        <span>
            {{ content }}
        </span>
    </component>
</template>
